// Copyright (c) StrangeBee 2023

import React, { ReactElement } from 'react';
import { Store } from 'antd/lib/form/interface';
import { Form, FormInstance } from 'antd';
import { DonutWidgetType, WidgetOutput, FilterInput, ObjectTypeV1 } from 'thehive-sdk';
import DividerBasics from '@Organisms/Dashboard/WidgetManage/WidgetFormCommon/DividerBasics';
import DividerFilter from '@Organisms/Dashboard/WidgetManage/WidgetFormCommon/DividerFilter';
import ProvidedWidgetFormFilter from '@Organisms/Dashboard/WidgetManage/WidgetFormFilter/ProvidedWidgetFormFilter';
import TitleField from '@Organisms/Dashboard/WidgetManage/WidgetFormField/TitleField';
import EntityField from '@Organisms/Dashboard/WidgetManage/WidgetFormField/EntityField';
import { THForm } from '@Molecules/THForm/THForm';
import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { CustomizeField } from '../CustomizeField';
import { useCustomize } from '../utils';
import { entitiesToExclude } from '@Constants/dashboard';
import { THSwitch } from '@Molecules/Inputs/THSwitch/THSwitch';
import { useLocalMessage } from '@Helpers/locale';

export interface DonutValues extends Store {
    title: string;
    entity: ObjectTypeV1;
    category: string;
    filters: FilterInput[];
    periodDateField?: string;
    limit?: number;
    sort?: string;
    customize?: any[];
    percentage?: boolean;
}

interface DonutFormProps {
    onFinish: (values: DonutValues) => void;
    defaultValues?: WidgetOutput & { options: DonutWidgetType };
    form: FormInstance;
    isValid?: boolean;
    onCancel: () => void;
    onChange: () => void;
}

export const DonutForm = ({
    onFinish,
    defaultValues,
    form,
    onCancel,
    onChange,
}: DonutFormProps): ReactElement | null => {
    const i = useLocalMessage();

    const entity = Form.useWatch(['entity'], form);
    const category = Form.useWatch(['category'], form);

    useCustomize({ entity, field: category, form });

    return (
        <THForm<DonutValues>
            name="donut-form"
            form={form}
            onFinish={onFinish}
            onValuesChange={onChange}
            initialValues={{
                title: defaultValues?.options.title,
                entity: defaultValues?.options.entity,
                category: defaultValues?.options.field,
                filters: defaultValues?.options.filters,
                periodDateField: defaultValues?.options.periodDateField || '_createdAt',
                limit: defaultValues?.options.limit,
                sort: defaultValues?.options.sort,
                customize: defaultValues?.options.customize,
                percentage: defaultValues?.options.percentage,
            }}
        >
            <THForm.Layout footer={<FormSubmitButton onCancel={onCancel} disabled={false} />}>
                <DividerBasics />
                <TitleField />
                <EntityField form={form} showCategory showPeriodDate entitiesToExclude={entitiesToExclude} />

                <DividerFilter />
                <THForm.Item name="filters">
                    <ProvidedWidgetFormFilter entity={entity} />
                </THForm.Item>

                <THForm.Item
                    name="percentage"
                    valuePropName="checked"
                    label={i('app.dashboard.widget.create.form.percentage.label')}
                >
                    <THSwitch />
                </THForm.Item>
                <CustomizeField />
            </THForm.Layout>
        </THForm>
    );
};
